import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Heart, Check } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUrl';

interface Product {
  id: number;
  name: string;
  slug: string;
  price: number;
  compare_price?: number;
  featured_image: string;
  is_new?: boolean;
  is_featured?: boolean;
  discount_percentage?: number;
  colors?: Array<{ name: string; hex: string }>;
  sizes?: string[];
  category?: { name: string; slug: string };
  product_type?: 'pet' | 'food' | 'accessory' | 'other';
  breed?: string;
  age?: string;
  vaccinated?: boolean;
  flavor?: string;
  weight?: string;
}

interface ProductCardProps {
  product: Product;
  className?: string;
}

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=400&q=70';

export default function ProductCard({ product, className = '' }: ProductCardProps) {
  const { addItem } = useCart();
  const [isWished, setIsWished] = useState(false);
  const [imageError, setImageError] = useState(false);

  const discount = product.compare_price && product.compare_price > product.price
    ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100)
    : 0;

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      id: Date.now(),
      productId: product.id,
      name: product.name,
      price: product.price,
      image: getImageUrl(product.featured_image) || FALLBACK_IMAGE,
      quantity: 1,
    });
    toast.success(`Added to cart!`, { duration: 2000 });
  };

  const handleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWished(w => !w);
    toast.success(isWished ? 'Removed from wishlist' : 'Added to wishlist', { duration: 1500 });
  };

  const formatPrice = (price: number) => `Rs.${price.toLocaleString()}`;
  const imgSrc = imageError ? FALLBACK_IMAGE : (getImageUrl(product.featured_image) || FALLBACK_IMAGE);

  // Determine specialty tag
  let specialtyTag = '';
  let specialtyColor = 'bg-accent/10 text-accent';
  if (product.product_type === 'pet') {
    specialtyTag = product.vaccinated ? 'Vet Certified' : 'Healthy Breed';
    specialtyColor = 'bg-green-500/10 text-green-700 border border-green-500/20';
  } else if (product.product_type === 'food') {
    specialtyTag = product.flavor ? `${product.flavor}` : 'Organic Nutrition';
    specialtyColor = 'bg-amber-500/10 text-amber-700 border border-amber-500/20';
  } else if (product.product_type === 'accessory') {
    specialtyTag = 'Premium Quality';
    specialtyColor = 'bg-blue-500/10 text-blue-700 border border-blue-500/20';
  }

  return (
    <div className={`group relative bg-white border border-gray-100/60 rounded-3xl overflow-hidden hover:shadow-premium hover:border-accent/10 transition-all duration-500 flex flex-col h-full ${className}`}>
      <Link to={`/products/${product.slug}`} className="block flex-1 flex flex-col">
        {/* Image Container */}
        <div className="relative overflow-hidden bg-[#FCFAF7] aspect-[4/5] rounded-t-3xl flex-shrink-0">
          <img
            src={imgSrc}
            alt={product.name}
            onError={() => setImageError(true)}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />

          {/* Soft tint gradient overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Badges (Left) */}
          <div className="absolute top-4 left-4 flex flex-col gap-1.5 z-10">
            {product.is_new && (
              <span className="bg-primary text-white text-[9px] font-extrabold px-2.5 py-1 tracking-wider rounded-full uppercase shadow-sm">NEW</span>
            )}
            {discount > 0 && (
              <span className="bg-red-600 text-white text-[9px] font-extrabold px-2.5 py-1 tracking-wider rounded-full shadow-sm">-{discount}%</span>
            )}
          </div>

          {/* Wishlist Button (Right) */}
          <button
            onClick={handleWishlist}
            className="absolute top-4 right-4 w-9 h-9 bg-white/95 backdrop-blur-md rounded-full flex items-center justify-center shadow-premium opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 hover:scale-110 active:scale-95 z-10"
            title={isWished ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart size={16} className={isWished ? 'fill-red-500 text-red-500' : 'text-gray-650 group-hover:text-primary transition-colors'} />
          </button>

          {/* Specialty Tag (Pill inside container) */}
          {specialtyTag && (
            <div className="absolute bottom-4 left-4 z-10">
              <span className={`text-[9px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${specialtyColor}`}>
                {specialtyTag}
              </span>
            </div>
          )}

          {/* Quick Add Button (Centered Slide/Fade-in Pill) */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
            <button
              onClick={handleQuickAdd}
              className="bg-primary hover:bg-accent text-white py-3 px-6 text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 rounded-2xl shadow-premium hover:scale-105 active:scale-95 transition-all duration-300"
            >
              <ShoppingBag size={13} />
              Quick Add
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-5 flex-1 flex flex-col justify-between">
          <div className="space-y-1.5">
            {product.category && (
              <p className="text-[9px] text-accent font-extrabold uppercase tracking-widest">{product.category.name}</p>
            )}
            <h3 className="text-sm font-bold text-primary leading-snug line-clamp-2 group-hover:text-accent transition-colors">
              {product.name}
            </h3>
            
            {/* Show details: breed or age for pets, flavor for food */}
            {(product.breed || product.age || product.flavor) && (
              <div className="flex flex-wrap gap-x-2 gap-y-1 pt-1">
                {product.breed && (
                  <span className="text-[10px] text-gray-500 font-medium">Breed: {product.breed}</span>
                )}
                {product.age && (
                  <span className="text-[10px] text-gray-400 font-medium">• {product.age}</span>
                )}
                {product.weight && (
                  <span className="text-[10px] text-gray-500 font-medium">{product.weight}</span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50 flex-shrink-0">
            <div className="flex items-baseline gap-2">
              <span className={`text-base font-extrabold ${discount > 0 ? 'text-red-600' : 'text-primary'}`}>
                {formatPrice(product.price)}
              </span>
              {product.compare_price && product.compare_price > product.price && (
                <span className="text-xs text-gray-400 line-through font-medium">{formatPrice(product.compare_price)}</span>
              )}
            </div>
            {product.product_type === 'pet' && (
              <span className="flex items-center gap-0.5 text-[10px] text-green-600 font-bold bg-green-500/10 px-2 py-0.5 rounded-full border border-green-500/10">
                <Check size={10} /> Certified
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
